import { useCallback, useEffect, useRef, useState } from 'react';
import { log } from '@/logger/logger';
import { isFigmaEmbedAllowedOrigin } from '@/consts/auth.consts';
import { decodeFigmaEmbedToken, type FigmaEmbedTokenPayload } from './figmaEmbedToken';
import type { AuthUser } from '@/types/auth.types';
import type { AuthProviderHook, SignInResult } from '../authProvider.types';

export type { FigmaEmbedPermission } from './figmaEmbedToken';

const logger = log.getLogger('useFigmaEmbedAuthProvider');

const TOKEN_REFRESH_LEAD_MS = 3 * 60 * 1000;
const TOKEN_REQUEST_TIMEOUT_MS = 5_000;

function buildAuthUserFromPayload(payload: FigmaEmbedTokenPayload, token: string): AuthUser {
  return {
    uid: `figma:${payload.sub}`,
    email: null,
    // Name/email/photo are not carried by embed tokens. The editor UI surfaces that show these
    // (share modal, Intercom, analytics identify) are hidden or N/A in embed mode.
    displayName: null,
    photoURL: null,
    accessToken: token,
    getIdToken: () => Promise.resolve(token),
  };
}

// TODO(figma-embed): consider refactoring the token fetch/refresh plumbing on top of
// TanStack `useQuery` — the query function wraps the postMessage request/response, a
// `refetchInterval` computed from `payload.exp - TOKEN_REFRESH_LEAD_MS` replaces the manual
// `setTimeout`/`scheduleProactiveRefresh`, and `status` plus the query's built-in in-flight
// dedup would remove the `pendingRequestRef` coalescing logic and the ad-hoc loading state.
// Non-blocking cleanup; leaving the explicit ref-based implementation for now since V1 is
// already verified end-to-end.
export const useFigmaEmbedAuthProvider = (): AuthProviderHook => {
  const [user, setUser] = useState<AuthUser | null>(null);
  const [accessToken, setAccessToken] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  const refreshTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const requestTokenRef = useRef<() => Promise<string>>(() => Promise.reject(new Error('not ready')));
  const pendingRequestRef = useRef<{
    resolve: (token: string) => void;
    reject: (reason: Error) => void;
    timer: ReturnType<typeof setTimeout>;
  } | null>(null);

  const clearRefreshTimer = useCallback(() => {
    if (refreshTimerRef.current !== null) {
      clearTimeout(refreshTimerRef.current);
      refreshTimerRef.current = null;
    }
  }, []);

  const scheduleProactiveRefresh = useCallback(
    (expSeconds: number) => {
      clearRefreshTimer();
      const expiresAtMs = expSeconds * 1000;
      const refreshAtMs = expiresAtMs - TOKEN_REFRESH_LEAD_MS;
      const delayMs = Math.max(0, refreshAtMs - Date.now());
      refreshTimerRef.current = setTimeout(() => {
        requestTokenRef.current().catch((error) => {
          logger.error('Proactive token refresh failed', error);
        });
      }, delayMs);
    },
    [clearRefreshTimer],
  );

  const requestTokenFromParent = useCallback((): Promise<string> => {
    if (pendingRequestRef.current) {
      return new Promise((resolve, reject) => {
        const prev = pendingRequestRef.current!;
        const prevResolve = prev.resolve;
        const prevReject = prev.reject;
        prev.resolve = (token) => {
          prevResolve(token);
          resolve(token);
        };
        prev.reject = (reason) => {
          prevReject(reason);
          reject(reason);
        };
      });
    }

    return new Promise((resolve, reject) => {
      if (window.parent === window) {
        reject(new Error('Not running inside an iframe — no parent to request token from'));
        return;
      }

      const timer = setTimeout(() => {
        const pending = pendingRequestRef.current;
        pendingRequestRef.current = null;
        pending?.reject(new Error('Timed out waiting for WEAVE_TOKEN_REFRESH from parent'));
      }, TOKEN_REQUEST_TIMEOUT_MS);

      pendingRequestRef.current = { resolve, reject, timer };
      window.parent.postMessage({ type: 'WEAVE_TOKEN_REQUEST' }, '*');
    });
  }, []);

  useEffect(() => {
    requestTokenRef.current = requestTokenFromParent;
  }, [requestTokenFromParent]);

  useEffect(() => {
    const handleMessage = (event: MessageEvent) => {
      if (!isFigmaEmbedAllowedOrigin(event.origin)) {
        return;
      }
      const data = event.data as { type?: string; token?: string } | null;
      if (!data || data.type !== 'WEAVE_TOKEN_REFRESH' || typeof data.token !== 'string') {
        return;
      }

      const payload = decodeFigmaEmbedToken(data.token);
      const pending = pendingRequestRef.current;

      // Malformed / undecodable token: fail loudly instead of stalling.
      //
      // Resolving the pending promise here would satisfy the initial `requestTokenFromParent()
      // .catch(setIsLoading(false))` without ever flipping `isLoading`, and since the timeout is
      // also cleared on arrival, the app would spin forever with no recovery path. Reject the
      // pending promise and flip `isLoading` so the upstream AuthProviderInner stops rendering
      // the spinner and drops into its unauthenticated branch.
      //
      // TODO(figma-embed): surface a dedicated error state so the UI can show an inline
      // "unable to load Weave — please reload" message instead of the default unauthenticated
      // shape, which in embed mode has no sensible sign-in path.
      if (!payload) {
        logger.error('Received token with invalid payload');
        setIsLoading(false);
        if (pending) {
          clearTimeout(pending.timer);
          pendingRequestRef.current = null;
          pending.reject(new Error('Received malformed token from parent'));
        }
        return;
      }

      setAccessToken(data.token);
      setUser(buildAuthUserFromPayload(payload, data.token));
      setIsLoading(false);
      scheduleProactiveRefresh(payload.exp);

      if (pending) {
        clearTimeout(pending.timer);
        pendingRequestRef.current = null;
        pending.resolve(data.token);
      }
    };

    window.addEventListener('message', handleMessage);
    requestTokenFromParent().catch((error) => {
      logger.error('Initial token request failed', error);
      setIsLoading(false);
    });

    return () => {
      window.removeEventListener('message', handleMessage);
      clearRefreshTimer();
      const pending = pendingRequestRef.current;
      if (pending) {
        clearTimeout(pending.timer);
        pendingRequestRef.current = null;
        pending.reject(new Error('FigmaEmbedAuthProvider unmounted before token was delivered'));
      }
    };
  }, [clearRefreshTimer, requestTokenFromParent, scheduleProactiveRefresh]);

  const signIn = useCallback((): Promise<SignInResult> => {
    return Promise.reject(new Error('signIn is not supported in Figma embed mode'));
  }, []);

  const signOut = useCallback(() => {
    clearRefreshTimer();
    setUser(null);
    setAccessToken(null);
    return Promise.resolve();
  }, [clearRefreshTimer]);

  const getCurrentUser = useCallback(() => user, [user]);

  const getAdditionalUserInfo = useCallback(() => null, []);

  const getIdToken = useCallback(async () => {
    if (accessToken) {
      const payload = decodeFigmaEmbedToken(accessToken);
      const notExpired = payload !== null && payload.exp * 1000 > Date.now() + 10_000;
      if (notExpired) return accessToken;
    }
    return requestTokenFromParent();
  }, [accessToken, requestTokenFromParent]);

  const getStoredPromotion = useCallback(() => null, []);

  const initiateRedirectSignIn = useCallback(() => {
    return Promise.reject(new Error('initiateRedirectSignIn is not supported in Figma embed mode'));
  }, []);

  return {
    signIn,
    signOut,
    initiateRedirectSignIn,
    getIdToken,
    getCurrentUser,
    getAdditionalUserInfo,
    getStoredPromotion,
    user,
    isLoading,
    accessToken,
  };
};
